'use client';

import { useCallback, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

import { buildApiUrl } from '@/lib/platformUtils';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';

import type {
  CallSettings,
  CallState,
  CallType,
  OperationsUser,
  SipExtension,
  SipCallData,
  ViewMode,
} from '@/components/call';

export type DialPadConnectToRoom = (
  roomName: string,
  mode: CallType,
  token?: string,
  host?: string,
) => Promise<boolean>;

export interface DialPadCallNotifications {
  playDialTone(): void;
  stopDialTone(): void;
}

export interface UseDialPadSubmitParams {
  callSettings: CallSettings | null;
  extensionList: SipExtension[];
  operationsUsers: OperationsUser[];
  parkingSlotRange: { start: number; end: number };
  /** The client ID used for outbound calling context (selected PABX client). */
  activeCallClientId: number;
  startCall: (user: OperationsUser, type: CallType) => Promise<void>;
  retrieveParkedCall: (slot: number) => Promise<void>;
  connectToRoom: DialPadConnectToRoom;
  callNotifications: DialPadCallNotifications;
  callNotificationsRef: MutableRefObject<DialPadCallNotifications | null>;

  setDialPadNumber: Dispatch<SetStateAction<string>>;
  setDialHistory: Dispatch<SetStateAction<string[]>>;
  setIsDialingPSTN: Dispatch<SetStateAction<boolean>>;
  setSelectedUser: Dispatch<SetStateAction<OperationsUser | null>>;
  setSipCallData: Dispatch<SetStateAction<SipCallData | null>>;
  setCallState: Dispatch<SetStateAction<CallState>>;
  setViewMode: Dispatch<SetStateAction<ViewMode>>;

  callIdRef: MutableRefObject<string | null>;
  sipCallLogIdRef: MutableRefObject<string | null>;
  ringTimeoutRef: MutableRefObject<NodeJS.Timeout | null>;
  callStateRef: MutableRefObject<CallState>;
  isSipCallRef: MutableRefObject<boolean>;
  endCallRef: MutableRefObject<((reason?: string) => Promise<void>) | null>;
}

export interface UseDialPadSubmitResult {
  dialPadSubmit: (number: string) => Promise<void>;
  /** Abort outbound API / hang up an outbound attempt still in progress (keypad loading or early initiating). */
  cancelOutboundDialInProgress: () => void;
}

/**
 * Keypad dialed-number handler: speed dial, parked slot retrieve, extension match,
 * or SIP outbound initiation (shared by Total Connect idle keypad).
 */
export function useDialPadSubmit({
  callSettings,
  extensionList,
  operationsUsers,
  parkingSlotRange,
  activeCallClientId,
  startCall,
  retrieveParkedCall,
  connectToRoom,
  callNotifications,
  callNotificationsRef,
  setDialPadNumber,
  setDialHistory,
  setIsDialingPSTN,
  setSelectedUser,
  setSipCallData,
  setCallState,
  setViewMode,
  callIdRef,
  sipCallLogIdRef,
  ringTimeoutRef,
  callStateRef,
  isSipCallRef,
  endCallRef,
}: UseDialPadSubmitParams): UseDialPadSubmitResult {
  const outboundAbortRef = useRef<AbortController | null>(null);
  const dialCancelRequestedRef = useRef(false);
  const latestOutboundAttemptRef = useRef<{
    call_log_id: string | null;
    provider_call_id: string | null;
    livekit_room: string | null;
  } | null>(null);

  const cancelOutboundDialInProgress = useCallback(() => {
    dialCancelRequestedRef.current = true;
    outboundAbortRef.current?.abort();
    outboundAbortRef.current = null;

    callNotificationsRef.current?.stopDialTone();
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }

    if (callStateRef.current !== 'idle') {
      void endCallRef.current?.('user_cancelled');
    } else {
      const pending = latestOutboundAttemptRef.current;
      if (pending?.call_log_id || pending?.provider_call_id || pending?.livekit_room) {
        const tokens = authService.getTokens();
        void fetch(buildApiUrl('/api/v1/sip'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokens?.access_token || ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'terminateCall',
            call_id: pending.call_log_id,
            provider_call_id: pending.provider_call_id,
            livekit_room: pending.livekit_room,
          }),
        }).catch(err =>
          console.error(
            '[TotalConnectRTC] terminateCall after keypad cancel:',
            err,
          ),
        );
      }
      latestOutboundAttemptRef.current = null;
    }

    setIsDialingPSTN(false);
  }, [
    callNotificationsRef,
    callStateRef,
    endCallRef,
    ringTimeoutRef,
    setIsDialingPSTN,
  ]);

  const dialPadSubmit = useCallback(
    async (rawNumber: string) => {
      let number = rawNumber;
      const trimmed = number.trim();

      const speedDial = callSettings?.speed_dials?.find(
        sd => sd.speed_dial_code === trimmed,
      );
      if (speedDial) {
        setDialPadNumber('');
        if (speedDial.target_type === 'extension') {
          const sdExt = extensionList.find(
            ext =>
              ext.extension_number === speedDial.target_value ||
              ext.friendly_extension === speedDial.target_value,
          );
          if (sdExt?.user_id) {
            const sdUser = operationsUsers.find(
              u => u.id === Number(sdExt.user_id),
            );
            if (sdUser) {
              void startCall(sdUser, 'voice');
              return;
            }
          }
          notify.error(`Extension ${speedDial.target_value} not found`);
          return;
        }
        number = speedDial.target_value;
      }

      const dialedNum = parseInt(number, 10);
      if (
        !Number.isNaN(dialedNum) &&
        dialedNum >= parkingSlotRange.start &&
        dialedNum <= parkingSlotRange.end
      ) {
        setDialPadNumber('');
        await retrieveParkedCall(dialedNum);
        return;
      }

      const matchedExtension = extensionList.find(
        ext =>
          ext.extension_number === trimmed ||
          ext.friendly_extension === trimmed,
      );
      if (matchedExtension?.user_id) {
        const targetUser = operationsUsers.find(
          u => u.id === Number(matchedExtension.user_id),
        );
        if (targetUser) {
          setDialPadNumber('');
          void startCall(targetUser, 'voice');
          return;
        }
      }

      const hasSipExtension = !!callSettings?.sip_extension?.extension_number?.trim();
      if (!hasSipExtension) {
        notify.error('External dialing requires a SIP extension. Dial an internal extension or use contacts for internal calls.');
        return;
      }

      if (callSettings?.sip_extension?.can_make_outbound_calls === false) {
        notify.error('Your extension is not permitted to make outbound calls');
        return;
      }

      const saveToDialHistory = (num: string) => {
        setDialHistory(prev => {
          const cleaned = num.trim();
          if (!cleaned) return prev;
          const next = [cleaned, ...prev.filter(n => n !== cleaned)].slice(
            0,
            20,
          );
          try {
            localStorage.setItem(
              'totalconnect_dial_history',
              JSON.stringify(next),
            );
          } catch {
            /* ignore quota / privacy */
          }
          return next;
        });
      };

      dialCancelRequestedRef.current = false;
      latestOutboundAttemptRef.current = null;
      const controller = new AbortController();
      outboundAbortRef.current = controller;

      setIsDialingPSTN(true);
      let outboundCallLogId: string | null = null;
      let outboundProviderCallId: string | null = null;
      let outboundRoomName: string | null = null;

      try {
        const tokens = authService.getTokens();
        const res = await fetch(buildApiUrl('/api/v1/sip'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokens?.access_token}`,
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
          body: JSON.stringify({
            action: 'initiateOutboundCall',
            to_number: number,
            trunk_id:
              callSettings?.sip_extension?.preferred_outbound_trunk_id ||
              undefined,
            client_id: activeCallClientId || undefined,
          }),
        });

        const data: {
          success?: boolean;
          error?: string;
          data?: {
            call_id?: string | null;
            provider_call_id?: string | null;
            room_name?: string | null;
            livekit_room?: string | null;
            livekit_token?: string | null;
            livekit_host?: string;
          };
        } = await res.json();

        if (data.success) {
          outboundCallLogId = data.data?.call_id ?? null;
          outboundProviderCallId = data.data?.provider_call_id ?? null;
          outboundRoomName =
            data.data?.room_name ?? data.data?.livekit_room ?? null;
          latestOutboundAttemptRef.current = {
            call_log_id: outboundCallLogId,
            provider_call_id: outboundProviderCallId,
            livekit_room: outboundRoomName,
          };

          if (dialCancelRequestedRef.current) {
            const tokens = authService.getTokens();
            void fetch(buildApiUrl('/api/v1/sip'), {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${tokens?.access_token || ''}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                action: 'terminateCall',
                call_id: outboundCallLogId,
                provider_call_id: outboundProviderCallId,
                livekit_room: outboundRoomName,
              }),
            }).catch(() => {});
            latestOutboundAttemptRef.current = null;
            dialCancelRequestedRef.current = false;
            return;
          }

          const outboundToken = data.data?.livekit_token ?? null;
          const outboundHost = data.data?.livekit_host;

          notify.success(`Calling ${number}...`);
          saveToDialHistory(number);
          callNotifications.playDialTone();
          callIdRef.current = outboundRoomName || outboundCallLogId;
          sipCallLogIdRef.current = outboundCallLogId;

          const externalCallee: OperationsUser = {
            id: 0,
            email: '',
            first_name: number,
            last_name: '',
            role: 'External',
            module_role: '',
            avatar_url: undefined,
          };
          setSelectedUser(externalCallee);
          setSipCallData({
            call_log_id: outboundCallLogId || undefined,
            provider_call_id: outboundProviderCallId || undefined,
            livekit_room: outboundRoomName || undefined,
            livekit_token: outboundToken || undefined,
            livekit_host: outboundHost,
            caller_number: number,
          });
          setCallState('initiating');
          callStateRef.current = 'initiating';
          isSipCallRef.current = true;
          setViewMode('incall');

          if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
          ringTimeoutRef.current = setTimeout(() => {
            if (
              callStateRef.current === 'initiating' &&
              callIdRef.current === (outboundRoomName || outboundCallLogId)
            ) {
              callNotificationsRef.current?.stopDialTone();
              void endCallRef.current?.('unanswered');
              notify.error('No answer');
            }
          }, 50_000);

          if (outboundRoomName && outboundToken) {
            if (dialCancelRequestedRef.current) {
              return;
            }
            const connected = await connectToRoom(
              outboundRoomName,
              'voice',
              outboundToken,
              outboundHost,
            );
            if (dialCancelRequestedRef.current) {
              return;
            }
            if (!connected) {
              throw new Error('Failed to connect to the outbound call room');
            }
          }

          latestOutboundAttemptRef.current = null;
          setDialPadNumber('');
        } else {
          notify.error(data.error || 'Failed to initiate call');
        }
      } catch (e: unknown) {
        const isAbort =
          (e instanceof DOMException && e.name === 'AbortError') ||
          (e instanceof Error && e.name === 'AbortError');
        if (isAbort || dialCancelRequestedRef.current) {
          dialCancelRequestedRef.current = false;
          callNotifications.stopDialTone();
          if (ringTimeoutRef.current) {
            clearTimeout(ringTimeoutRef.current);
            ringTimeoutRef.current = null;
          }
          outboundAbortRef.current = null;
          return;
        }

        callNotifications.stopDialTone();
        if (ringTimeoutRef.current) {
          clearTimeout(ringTimeoutRef.current);
          ringTimeoutRef.current = null;
        }

        if (
          outboundCallLogId ||
          outboundProviderCallId ||
          outboundRoomName
        ) {
          const tokens = authService.getTokens();
          void fetch(buildApiUrl('/api/v1/sip'), {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${tokens?.access_token || ''}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'terminateCall',
              call_id: outboundCallLogId,
              provider_call_id: outboundProviderCallId,
              livekit_room: outboundRoomName,
            }),
          }).catch(err =>
            console.error(
              '[TotalConnectRTC] Failed to clean up outbound SIP call:',
              err,
            ),
          );
        }

        callIdRef.current = null;
        sipCallLogIdRef.current = null;
        isSipCallRef.current = false;
        setSipCallData(null);
        setSelectedUser(null);
        setCallState('idle');
        callStateRef.current = 'idle';
        setViewMode('keypad');
        notify.error('Failed to make call');
      } finally {
        outboundAbortRef.current = null;
        dialCancelRequestedRef.current = false;
        setIsDialingPSTN(false);
      }
    },
    [
      callSettings,
      extensionList,
      operationsUsers,
      parkingSlotRange,
      activeCallClientId,
      startCall,
      retrieveParkedCall,
      connectToRoom,
      callNotifications,
      callNotificationsRef,
      setDialPadNumber,
      setDialHistory,
      setIsDialingPSTN,
      setSelectedUser,
      setSipCallData,
      setCallState,
      setViewMode,
      callIdRef,
      sipCallLogIdRef,
      ringTimeoutRef,
      callStateRef,
      isSipCallRef,
      endCallRef,
    ],
  );

  return { dialPadSubmit, cancelOutboundDialInProgress };
}
